// Thin ExcelJS wrapper: workbook <-> arrays of plain cell values. Works in the browser and in Node (tests).
import ExcelJS from "exceljs";

export type Cell = string | number | boolean | Date | null;
export interface Sheet {
  name: string;
  rows: Cell[][];
}

function plain(v: ExcelJS.CellValue): Cell {
  if (v === null || v === undefined) return null;
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    if ("result" in v) return plain((v as ExcelJS.CellFormulaValue).result as ExcelJS.CellValue);
    if ("richText" in v) return (v as ExcelJS.CellRichTextValue).richText.map((r) => r.text).join("");
    if ("text" in v) return String((v as ExcelJS.CellHyperlinkValue).text);
    if ("error" in v) return null;
    return null;
  }
  return v as Cell;
}

export async function readWorkbook(data: ArrayBuffer | Uint8Array): Promise<Sheet[]> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(data as ArrayBuffer);
  return wb.worksheets.map((ws) => {
    const rows: Cell[][] = [];
    ws.eachRow({ includeEmpty: true }, (row, n) => {
      const vals = row.values as ExcelJS.CellValue[];
      rows[n - 1] = vals.slice(1).map(plain);
    });
    for (let i = 0; i < rows.length; i++) rows[i] ??= [];
    return { name: ws.name, rows };
  });
}

export interface OutSheet {
  name: string;
  rows: Cell[][];
  /** 0-based row index of the header row to style and freeze. */
  headerRow?: number;
  widths?: number[];
  numFmt?: Record<number, string>; // column index -> number format
}

export async function writeWorkbook(sheets: OutSheet[]): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Mongoose EVM";
  for (const s of sheets) {
    const ws = wb.addWorksheet(s.name.slice(0, 31).replace(/[\\/?*[\]:]/g, " "));
    for (const r of s.rows) ws.addRow(r.map((c) => (c === null ? undefined : c)));
    if (s.headerRow !== undefined) {
      const hr = ws.getRow(s.headerRow + 1);
      hr.font = { bold: true };
      hr.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EEF6" } };
      ws.views = [{ state: "frozen", ySplit: s.headerRow + 1 }];
    }
    const nCols = Math.max(0, ...s.rows.map((r) => r.length));
    for (let c = 0; c < nCols; c++) {
      const col = ws.getColumn(c + 1);
      col.width = s.widths?.[c] ?? Math.min(40, Math.max(10, ...s.rows.slice(0, 200).map((r) => String(r[c] ?? "").length + 2)));
      if (s.numFmt?.[c]) col.numFmt = s.numFmt[c];
    }
    // Number formats (Cesar, Oct 20: "keep them down to 2 digits after the decimal"): percentages as 0.00%, other
    // fractional numbers with two decimals. A column is a percentage when its header says "%"; a row is when one of
    // its label cells (first three columns) does, as in the period tables.
    const head = s.headerRow !== undefined ? s.rows[s.headerRow] ?? [] : [];
    const isPct = (v: Cell) => typeof v === "string" && /%/.test(v);
    ws.eachRow((row, n) => {
      const r = n - 1;
      const rowPct = r > (s.headerRow ?? -1) && (s.rows[r] ?? []).slice(0, 3).some(isPct);
      row.eachCell((cell, c) => {
        if (cell.value instanceof Date) cell.numFmt = "dd-mmm-yy";
        else if (typeof cell.value === "number" && !s.numFmt?.[c - 1] && r > (s.headerRow ?? -1)) {
          if (isPct(head[c - 1]) || rowPct) cell.numFmt = "0.00%";
          else if (!Number.isInteger(cell.value)) cell.numFmt = "#,##0.00";
        }
      });
    });
  }
  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

/** ISO date -> Date at UTC midnight, for writing real Excel dates. */
export function xlDate(iso: string | null | undefined): Date | null {
  return iso ? new Date(iso + "T00:00:00Z") : null;
}

export function download(buf: ArrayBuffer, filename: string) {
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function pickFile(accept = ".xlsx"): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}
